import React, { useState, useEffect } from 'react';
import { Form, Input, DatePicker, Select, Card, Button, message, Typography, Space, Upload, Spin, Modal, Alert } from 'antd';
import { UploadOutlined, LoadingOutlined, PlusOutlined, ExclamationCircleOutlined, BellFilled, CheckCircleFilled, EnvironmentOutlined, BookOutlined, CloseOutlined } from '@ant-design/icons';
import axios from '../../utils/axiosConfig';
import config from '../../config';
import StudentProfileForm from './subComponents/StudentProfileForm';
import StudentProfileFormSteps from './subComponents/StudentProfileFormSteps';
import StudentProfileReadOnly from './StudentProfileReadOnly';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../Layout/AdminLayout';
import UserLayout from '../Layout/UserLayout';

const { Title } = Typography;
const { Option } = Select;

const StudentProfile = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [messageQueue, setMessageQueue] = useState([]);
    const [initialLoading, setInitialLoading] = useState(true);
    const [data, setData] = useState({
        periods: [],
        subjects: [],
        gradeFields: [],
        initialValues: null,
        subjectTypes: []
    });
    const navigate = useNavigate();
    const [attachmentTypes, setAttachmentTypes] = useState([]);
    const [showNoticeModal, setShowNoticeModal] = useState(false);
    const [entryNoticeVisible, setEntryNoticeVisible] = useState(false);
    const [uploadLoading, setUploadLoading] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});
    const [uploadStatus, setUploadStatus] = useState({});
    const [fileList, setFileList] = useState({});
    const [studentProfileData, setStudentProfileData] = useState(null);
    const [pendingUploads, setPendingUploads] = useState({}); // Files waiting to be uploaded

    // Check authentication and user role
    const checkAuth = () => {
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user'));

        if (!token) {
            message.error('Vui lòng đăng nhập để tiếp tục!');
            navigate('/login', { state: { from: '/student-profile' } });
            return false;
        }

        return {
            isAuthenticated: true,
            isAdmin: user?.roles?.[0]?.name === 'admin'
        };
    };

    // Fetch all necessary data
    useEffect(() => {
        const auth = checkAuth();
        if (!auth.isAuthenticated) return;

        const fetchData = async () => {
            try {
                setInitialLoading(true);
                const token = localStorage.getItem('token');
                const [
                    periodsResponse,
                    profileResponse,
                    subjectsResponse,
                    
                    gradesResponse,
                    attachmentTypesResponse,
                    subjectTypesResponse
                ] = await Promise.all([
                    axios.get(`${config.apiUrl}/api/v1/periods`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${config.apiUrl}/api/v1/student-profile`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${config.apiUrl}/api/v1/subjects`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${config.apiUrl}/api/v1/student-grades`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${config.apiUrl}/api/v1/attachment-types`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }),
                    axios.get(`${config.apiUrl}/api/v1/subject-types`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                ]);

                // Set attachment types
                setAttachmentTypes(attachmentTypesResponse.data.data || []);

                // Handle profile data and attachments FIRST
                let transformedInitialValues = null;
                
                if (profileResponse.data.data) {                    
                    const profile = profileResponse.data.data;
                    
                    // Save profile data for AttachmentsSection filtering
                    setStudentProfileData(profile);

                    // Helper function to normalize null values from backend to undefined
                    // This ensures consistency with Ant Design's allowClear behavior (which sets undefined)
                    const normalizeNull = (value) => (value === null || value === 'Không có' || value === '') ? undefined : value;

                    // Loại trừ các nested relation objects trước khi spread vào form
                    // để tránh "circular references" warning từ Ant Design Form
                    const {
                        attachments: _att,
                        grades: _grades,
                        admissions: _adm,
                        firstAdmission: _first,
                        period: _period,
                        payment: _payment,
                        province: _province,
                        commune: _commune,
                        subject_type: _subjectType,
                        ...profileFields
                    } = profile;

                    const formValues = {
                        ...profileFields,
                        ..._grades,
                        date_of_birth: profile.date_of_birth ? dayjs(profile.date_of_birth) : null,
                        identification_verify: profile.identification,
                        provincial_prize_subject_id: normalizeNull(profile.provincial_prize_subject_id),
                        provincial_prize: normalizeNull(profile.provincial_prize),
                        provincial_prize_grade: normalizeNull(profile.provincial_prize_grade),
                        admission_group: normalizeNull(profile.admission_group),
                        ielts_grade: normalizeNull(profile.ielts_grade),
                        subject_type_id: profile.subject_type_id,
                        // Chuyển admissions thành dạng phẳng để Form.List hiển thị đúng
                        admissions: (_adm ?? []).map(a => ({
                            subject_id: a.subject_id,
                            subject_type_id: a.subject?.subject_type_id ?? null,
                        })),
                    };
                
                    // Initialize file list from existing attachments
                    if (profile.attachments && Array.isArray(profile.attachments)) {
                        const initialFileList = {};
                        const initialUploadStatus = {};
                        profile.attachments.forEach(attachment => {
                            initialFileList[attachment.attachment_type_id] = [{
                                uid: attachment.id,
                                name: attachment.file_name,
                                status: 'done',
                                url: attachment.file_url,
                                thumbUrl: attachment.file_url,
                                type: attachment.mime_type,
                                size: attachment.file_size,
                                uploaded: true
                            }];
                            initialUploadStatus[attachment.attachment_type_id] = 'success';

                            // Set the attachment ID in form values
                            formValues[`attachment_${attachment.attachment_type_id}`] = attachment.id;
                        });
                        setFileList(initialFileList);
                        setUploadStatus(initialUploadStatus);
                    }

                    // Save transformed values to use as initialValues
                    transformedInitialValues = formValues;

                    // Set form values
                    form.setFieldsValue(formValues);
                }

                // Set other data (with transformed initialValues)
                setData({
                    periods: periodsResponse.data.data || [],
                    subjects: subjectsResponse.data?.data || [],
                    subjectTypes: subjectTypesResponse.data.data || [],
                    gradeFields: gradesResponse.data.data || [],
                    initialValues: transformedInitialValues,
                });
            } catch (error) {
                console.error('Error fetching data:', error);
                if (error.response?.status === 401) {
                    message.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!');
                    navigate('/login');
                } else {
                    message.error('Không thể tải dữ liệu. Vui lòng thử lại!');
                }
            } finally {
                setInitialLoading(false);
            }
        };

        fetchData().then(() => {
            if (!sessionStorage.getItem('entry_notice_shown')) {
                setEntryNoticeVisible(true);
                sessionStorage.setItem('entry_notice_shown', '1');
            }
        });
    }, [form, navigate]);

    // Handle message queue
    useEffect(() => {
        if (messageQueue.length > 0) {
            const { type, content } = messageQueue[0];
            messageApi[type](content);
            setMessageQueue(prev => prev.slice(1));
        }
    }, [messageQueue, messageApi]);

    const showMessage = (content, type = 'success') => {
        setMessageQueue(prev => [...prev, { type, content }]);
    };

    // Handle file selection (auto upload with compression)
    const handleFileSelect = async (typeId, file) => {
        // Create preview URL
        const reader = new FileReader();
        reader.onload = async (e) => {
            const newFile = {
                uid: `temp-${Date.now()}`,
                name: file.name,
                status: 'uploading',
                originFileObj: file,
                thumbUrl: e.target.result,
                size: file.size,
                type: file.type
            };

            setFileList(prev => ({
                ...prev,
                [typeId]: [newFile]
            }));

            setUploadStatus(prev => ({
                ...prev,
                [typeId]: 'uploading'
            }));

            // Auto compress and upload
            let fileToUpload = file;
            
            // Compress images
            if (file.type.startsWith('image/')) {
                try {
                    const imageCompression = require('browser-image-compression');
                    const options = {
                        maxSizeMB: 1,
                        maxWidthOrHeight: 1920,
                        useWebWorker: true,
                        fileType: 'image/jpeg',
                        initialQuality: 0.8
                    };
                    
                    const compressedFile = await imageCompression.default(file, options);
                    const newFileName = file.name.replace(/\.[^.]+$/, '.jpg');
                    fileToUpload = new File([compressedFile], newFileName, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    });
                } catch (error) {
                    console.error('Compression error:', error);
                    // Use original file if compression fails
                }
            }

            // Auto upload immediately
            await handleUploadWithProgress(fileToUpload, typeId);
        };
        reader.readAsDataURL(file);
    };

    // Handle manual upload for a single file
    const handleManualUpload = async (typeId, file) => {
        try {
            await handleUploadWithProgress(file, typeId);
        } catch (error) {
            console.error('Upload error:', error);
        }
    };

    // Handle batch upload - upload all pending files
    const handleBatchUpload = async () => {
        const pendingTypes = Object.keys(pendingUploads);
        
        if (pendingTypes.length === 0) {
            showMessage('Không có file nào cần tải lên!', 'info');
            return;
        }

        showMessage(`Đang tải lên ${pendingTypes.length} file...`, 'info');

        const uploadPromises = pendingTypes.map(async (typeId) => {
            const file = pendingUploads[typeId];
            if (file) {
                try {
                    await handleUploadWithProgress(file, typeId);
                } catch (error) {
                    console.error(`Error uploading type ${typeId}:`, error);
                }
            }
        });

        await Promise.all(uploadPromises);
        
        showMessage('Đã tải lên tất cả các file!', 'success');
    };

    // Upload with progress tracking
    const handleUploadWithProgress = async (file, typeId) => {
        try {
            setUploadLoading(prev => ({ ...prev, [typeId]: true }));
            setUploadStatus(prev => ({ ...prev, [typeId]: 'uploading' }));
            setUploadProgress(prev => ({ ...prev, [typeId]: 0 }));

            const formData = new FormData();
            formData.append('file', file);
            formData.append('attachment_type_id', typeId);

            const response = await axios.post(
                `${config.apiUrl}/api/v1/student-attachments`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    timeout: 300000,
                    maxContentLength: 20 * 1024 * 1024,
                    maxBodyLength: 20 * 1024 * 1024,
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(prev => ({ 
                            ...prev, 
                            [typeId]: percentCompleted 
                        }));
                    }
                }
            );

            if (response.data.success) {
                const attachmentType = attachmentTypes.find(type => type.id === typeId);
                showMessage(`Tải lên ${attachmentType?.name || 'tài liệu'} thành công!`);

                // Update file list with uploaded file info
                const fileUrl = response.data.data.attachment.file_url;
                setFileList(prev => ({
                    ...prev,
                    [typeId]: [{
                        uid: response.data.data.attachment.id,
                        name: response.data.data.attachment.file_name,
                        status: 'done',
                        url: fileUrl,
                        thumbUrl: fileUrl, // Add thumbUrl for image display
                        type: response.data.data.attachment.mime_type,
                        size: response.data.data.attachment.file_size * 1024 * 1024, // Convert MB to bytes
                        uploaded: true
                    }]
                }));

                // Update form with attachment ID
                form.setFieldsValue({
                    [`attachment_${typeId}`]: response.data.data.attachment.id
                });

                // Clear pending upload
                setPendingUploads(prev => {
                    const newPending = { ...prev };
                    delete newPending[typeId];
                    return newPending;
                });

                setUploadStatus(prev => ({ ...prev, [typeId]: 'success' }));
                setUploadProgress(prev => ({ ...prev, [typeId]: 100 }));

                return {
                    id: response.data.data.attachment.id,
                    file_path: response.data.data.attachment.file_url,
                    file_name: response.data.data.attachment.file_name
                };
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            const attachmentType = attachmentTypes.find(type => type.id === typeId);

            setUploadStatus(prev => ({ ...prev, [typeId]: 'error' }));

            if (error.response) {
                switch (error.response.status) {
                    case 413:
                        showMessage('Kích thước file quá lớn. Vui lòng chọn file nhỏ hơn 20MB!', 'error');
                        break;
                    case 422:
                        const errorData = error.response.data;
                        if (errorData.errors) {
                            Object.entries(errorData.errors).forEach(([field, message]) => {
                                showMessage(message, 'error');
                            });
                        } else {
                            showMessage(errorData.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                        }
                        break;
                    case 404:
                        showMessage('Không tìm thấy thông tin sinh viên. Vui lòng thử lại sau!', 'error');
                        break;
                    case 401:
                        showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                        localStorage.removeItem('token');
                        navigate('/login', { state: { from: '/student-profile' } });
                        break;
                    case 403:
                        if (error.response.data.error_code === 'PERIOD_NOT_ACTIVE') {
                            showMessage(error.response.data.message || 'Đợt tuyển sinh này hiện không mở, vui lòng thử lại sau.', 'error');
                        } else {
                            showMessage(error.response.data.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                        }
                        break;
                    default:
                        showMessage(error.response.data.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                }
            } else if (error.code === 'ECONNABORTED') {
                showMessage('Thời gian tải lên quá lâu. Vui lòng thử lại!', 'error');
            } else {
                showMessage(`Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}: ${error.message}`, 'error');
            }
            return null;
        } finally {
            setUploadLoading(prev => ({ ...prev, [typeId]: false }));
        }
    };

    // Function to handle file upload
    const handleUpload = async (file, typeId) => {

        try {
            setUploadLoading(prev => ({ ...prev, [typeId]: true }));

            const formData = new FormData();
            formData.append('file', file);
            formData.append('attachment_type_id', typeId);

            const response = await axios.post(
                `${config.apiUrl}/api/v1/student-attachments`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    // Add timeout and maxContentLength for large files
                    timeout: 300000, // 5 minutes
                    maxContentLength: 20 * 1024 * 1024, // 20MB
                    maxBodyLength: 20 * 1024 * 1024, // 20MB
                }
            );

            if (response.data.success) {
                const attachmentType = attachmentTypes.find(type => type.id === typeId);
                showMessage(`Tải lên ${attachmentType?.name || 'tài liệu'} thành công!`);

                return {
                    id: response.data.data.attachment.id,
                    file_path: response.data.data.attachment.file_url,
                    file_name: response.data.data.attachment.file_name
                };
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            const attachmentType = attachmentTypes.find(type => type.id === typeId);

            if (error.response) {
                // Handle specific error responses
                switch (error.response.status) {
                    case 413:
                        showMessage('Kích thước file quá lớn. Vui lòng chọn file nhỏ hơn 20MB!', 'error');
                        break;
                    case 422:
                        const errorData = error.response.data;
                        if (errorData.errors) {
                            Object.entries(errorData.errors).forEach(([field, message]) => {
                                showMessage(message, 'error');
                            });
                        } else {
                            showMessage(errorData.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                        }
                        break;
                    case 404:
                        showMessage('Không tìm thấy thông tin sinh viên. Vui lòng thử lại sau!', 'error');
                        break;
                    case 401:
                        showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                        localStorage.removeItem('token');
                        navigate('/login', { state: { from: '/student-profile' } });
                        break;
                    case 403:
                        if (error.response.data.error_code === 'PERIOD_NOT_ACTIVE') {
                            showMessage(error.response.data.message || 'Đợt tuyển sinh này hiện không mở, vui lòng thử lại sau.', 'error');
                        } else {
                            showMessage(error.response.data.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                        }
                        break;
                    default:
                        showMessage(error.response.data.message || `Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}!`, 'error');
                }
            } else if (error.code === 'ECONNABORTED') {
                showMessage('Thời gian tải lên quá lâu. Vui lòng thử lại!', 'error');
            } else {
                showMessage(`Lỗi khi tải lên ${attachmentType?.name || 'tài liệu'}: ${error.message}`, 'error');
            }
            return null;
        } finally {
            setUploadLoading(prev => ({ ...prev, [typeId]: false }));
        }
    };

    // Updated upload props to handle separate file uploads
    const uploadProps = (typeId) => ({
        listType: "picture-card",
        maxCount: 1,
        accept: 'image/jpeg,image/png,image/gif,image/heic,image/heif,application/pdf',
        fileList: fileList[typeId] || [],
        customRequest: async ({ file, onSuccess, onError }) => {

            try {
                const result = await handleUpload(file, typeId);
                if (result) {
                    // Update form with attachment ID
                    form.setFieldsValue({
                        [`attachment_${typeId}`]: result.id
                    });
                    // Clear validation message
                    form.setFields([{
                        name: `attachment_${typeId}`,
                        errors: []
                    }]);
                    onSuccess(result, file);
                } else {
                    showMessage('Lỗi khi tải lên!', 'error');
                }
            } catch (error) {
                onError(error);
            }
        },
        beforeUpload: (file) => {

            const isImage = file.type.startsWith('image/');
            const isPDF = file.type === 'application/pdf';

            if (!isImage && !isPDF) {
                showMessage('Chỉ được tải lên file ảnh hoặc PDF!', 'error');
                return false;
            }

            const isLt20M = file.size / 1024 / 1024 > 20;

            if (isLt20M) {
                message.error('Kích thước file không được vượt quá 20MB!');
                return false;
            }

            return true;
        },
        onChange: (info) => {
            setFileList(prev => ({
                ...prev,
                [typeId]: info.fileList
            }));

            // Clear validation message when file is selected
            if (info.fileList.length > 0) {
                form.setFields([{
                    name: `attachment_${typeId}`,
                    errors: []
                }]);
            }
        },
        onRemove: async (file) => {
            try {
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('No authentication token found');
                }

                // Get the attachment ID from the file or from fileList
                let attachmentId = file?.uid;
                
                // If file.uid is undefined, try to get from fileList
                if (!attachmentId) {
                    const currentFile = fileList[typeId]?.[0];
                    attachmentId = currentFile?.uid;
                }

                // If still no attachmentId, try to get from form values
                if (!attachmentId) {
                    const formValues = form.getFieldsValue();
                    attachmentId = formValues[`attachment_${typeId}`];
                }

                if (!attachmentId) {
                    showMessage('Không tìm thấy file cần xóa!', 'error');
                    return false;
                }

                // Call the API to delete the file
                const response = await axios.delete(
                    `${config.apiUrl}/api/v1/student-attachments/${attachmentId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.data.success) {
                    // Clear fileList for this type
                    setFileList(prev => {
                        const newFileList = { ...prev };
                        delete newFileList[typeId];
                        return newFileList;
                    });

                    // Clear upload status
                    setUploadStatus(prev => {
                        const newStatus = { ...prev };
                        delete newStatus[typeId];
                        return newStatus;
                    });

                    // Clear the attachment ID when file is removed
                    form.setFieldsValue({
                        [`attachment_${typeId}`]: undefined
                    });
                    // Clear validation message
                    form.setFields([{
                        name: `attachment_${typeId}`,
                        errors: []
                    }]);
                    // Show success message
                    showMessage('Xóa file thành công!');
                    return true;
                } else {
                    throw new Error(response.data.message || 'Xóa file thất bại!');
                }
            } catch (error) {
                console.error('Error removing file:', error);
                if (error.response) {
                    switch (error.response.status) {
                        case 401:
                            showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                            localStorage.removeItem('token');
                            navigate('/login', { state: { from: '/student-profile' } });
                            break;
                        case 404:
                            showMessage('Không tìm thấy file cần xóa!', 'error');
                            break;
                        default:
                            showMessage(error.response.data.message || 'Có lỗi xảy ra khi xóa file!', 'error');
                    }
                } else {
                    showMessage('Có lỗi xảy ra khi xóa file: ' + (error.message || 'Lỗi không xác định'), 'error');
                }
                return false;
            }
        }
    });

    const onFinish = async (values) => {
        try {
            setLoading(true);
            
            // Show loading message
            messageApi.loading({
                content: 'Đang lưu thông tin, vui lòng chờ...',
                duration: 0,
                key: 'submit-loading'
            });

            // Extract grades data
            const grades = {};
            Object.keys(values).forEach(key => {
                if (key.match(/^[a-z_]+_(?:10|11|12)$/)) {
                    grades[key] = parseFloat(values[key]);
                }
            });

            // Extract attachments
            const attachments = Object.entries(values)
                .filter(([key, value]) => key.startsWith('attachment_') && value)
                .map(([key, value]) => ({
                    attachment_type_id: parseInt(key.replace('attachment_', '')),
                    id: value
                }));

            // Extract admissions data, filter out entries without subject_id
            const admissions = (values.admissions || [])
                .filter(admission => admission.subject_id != null)
                .map((admission, index) => ({
                    subject_id: admission.subject_id,
                    priority: index + 2 // Start from 2 since first_admission_id is priority 1
                }));

            // Helper function to normalize values: convert empty string, undefined, and null to null for backend
            const normalizeValue = (value) => {
                if (value === '' || value === undefined || value === null) {
                    return null;
                }
                return value;
            };

            // Format the request payload
            const formattedValues = {
                student_profile: {
                    identification: values.identification,
                    full_name: values.full_name,
                    date_of_birth: values.date_of_birth 
                        ? (dayjs.isDayjs(values.date_of_birth) 
                            ? values.date_of_birth.format('DD/MM/YYYY')
                            : dayjs(values.date_of_birth).format('DD/MM/YYYY'))
                        : null,
                    contact_address: values.contact_address,
                    province_id: values.province_id,
                    commune_id: values.commune_id,
                    student_phone_number: values.student_phone_number,
                    parents_phone_number: values.parents_phone_number,
                    admission_area: values.admission_area,
                    admission_group: normalizeValue(values.admission_group), // Nullable field
                    admission: values.admission,
                    ielts_grade: normalizeValue(values.ielts_grade), // Nullable field
                    provincial_prize: normalizeValue(values.provincial_prize), // Nullable field
                    provincial_prize_subject_id: normalizeValue(values.provincial_prize_subject_id), // Nullable field
                    provincial_prize_grade: normalizeValue(values.provincial_prize_grade), // Nullable field
                    graduation_year: values.graduation_year,
                    grade_10_rank: values.grade_10_rank,
                    grade_11_rank: values.grade_11_rank,
                    grade_12_rank: values.grade_12_rank,
                    period_id: values.period_id,
                    first_admission_id: values.first_admission_id,
                    is_HSA: values.is_HSA || false,
                    is_TSA: values.is_TSA || false,
                    is_SPT: values.is_SPT || false,
                },
                grades: grades,
                attachments: attachments,
                admissions: admissions
            };            

            const token = localStorage.getItem('token');
            if (!token) {
                messageApi.destroy('submit-loading');
                throw new Error('No authentication token found');
            }

            // Submit the student profile data
            const response = await axios.post(
                `${config.apiUrl}/api/v1/student-profile`,
                formattedValues,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (response?.data?.success) {
                // Replace loading with success message (same key = smooth transition)
                messageApi.success({
                    content: '🎉 Cập nhật thông tin thành công!',
                    duration: 3,
                    key: 'submit-loading',
                    style: {
                        marginTop: '20vh',
                        fontSize: '16px',
                    }
                });

                setShowNoticeModal(true);
                
                // Refresh the data after successful update (in background)
                try {
                    const profileResponse = await axios.get(
                        `${config.apiUrl}/api/v1/student-profile`,
                        {
                            headers: { 'Authorization': `Bearer ${token}` }
                        }
                    );

                    if (profileResponse.data.data) {
                        const profile = profileResponse.data.data;
                        const updatedFormValues = {
                            ...profile,
                            ...profile.grades,
                            identification_verify: profile.identification,
                            date_of_birth: profile.date_of_birth ? dayjs(profile.date_of_birth) : null,
                            first_admission_id: profile.first_admission_id,
                            admissions: profile.admissions
                        };

                        // Update file list if there are attachments
                        if (profile.attachments && Array.isArray(profile.attachments)) {
                            const newFileList = {};
                            const newUploadStatus = {};
                            profile.attachments.forEach(attachment => {
                                newFileList[attachment.attachment_type_id] = [{
                                    uid: attachment.id,
                                    name: attachment.file_name,
                                    status: 'done',
                                    url: attachment.file_url,
                                    thumbUrl: attachment.file_url,
                                    type: attachment.mime_type,
                                    size: attachment.file_size,
                                    uploaded: true
                                }];
                                newUploadStatus[attachment.attachment_type_id] = 'success';

                                // Set attachment ID in form
                                updatedFormValues[`attachment_${attachment.attachment_type_id}`] = attachment.id;
                            });
                            setFileList(newFileList);
                            setUploadStatus(newUploadStatus);
                        }
                        
                        form.setFieldsValue(updatedFormValues);
                    }
                } catch (refreshError) {
                    // Silent fail - data refresh error doesn't affect submission
                    console.error('Error refreshing profile data:', refreshError);
                }
            } else {
                messageApi.destroy('submit-loading');
                throw new Error(response.data.message || 'Cập nhật thất bại!');
                }
        } catch (error) {
            console.error('Error updating profile:', error);
            
            // Hide loading message on error
            messageApi.destroy('submit-loading');
            
                if (error.response) {
                    // Handle specific error responses
                    switch (error.response.status) {
                        case 401:
                            showMessage('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại!', 'error');
                            localStorage.removeItem('token');
                            navigate('/login', { state: { from: '/student-profile' } });
                            break;
                        case 422:
                            // Validation errors
                            const errorMessages = error.response.data.errors
                                ? Object.values(error.response.data.errors).flat()
                                : ['Dữ liệu không hợp lệ'];
                            errorMessages.forEach(err => showMessage(err, 'error'));
                            break;
                        case 403:
                            if (error.response.data.error_code === 'PERIOD_NOT_ACTIVE') {
                                showMessage(error.response.data.message || 'Đợt tuyển sinh này hiện không mở, vui lòng thử lại sau.', 'error');
                            } else {
                                showMessage(error.response.data.message || 'Bạn không có quyền thực hiện thao tác này.', 'error');
                            }
                            break;
                        case 500:
                            // Handle duplicate identification error
                            if (error.response.data.error_code === 'DUPLICATE_IDENTIFICATION') {
                                showMessage('Số CCCD/CMND đã được đăng ký trước đó.', 'error');
                                // Set error on the identification field
                                form.setFields([{
                                    name: 'identification',
                                    errors: ['Số CCCD/CMND đã được đăng ký trước đó.']
                                }]);
                            } else {
                                showMessage(error.response.data.message || 'Có lỗi xảy ra khi cập nhật thông tin!', 'error');
                            }
                            break;
                        default:
                            showMessage(error.response.data.message || 'Có lỗi xảy ra khi cập nhật thông tin!', 'error');
                    }
                } else {
                    showMessage('Có lỗi xảy ra khi cập nhật thông tin: ' + (error.message || 'Lỗi không xác định'), 'error');
                }
        } finally {
            setLoading(false);
        }
    };

    const periodLocked = studentProfileData?.period
        && typeof studentProfileData.period === 'object'
        && studentProfileData.period.status !== 'active';

    const content = (
        <>
            {contextHolder}

            {/* Entry notice modal — shown once per session */}
            <Modal
                open={entryNoticeVisible}
                onCancel={() => setEntryNoticeVisible(false)}
                footer={
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => setEntryNoticeVisible(false)}
                        style={{
                            width: '100%',
                            height: 44,
                            background: '#262262',
                            borderColor: '#262262',
                            fontWeight: 600,
                            fontSize: 15,
                        }}
                    >
                        Đã hiểu, tiếp tục đăng ký
                    </Button>
                }
                title={null}
                width={520}
                centered
                styles={{ body: { padding: 0 } }}
            >
                {/* Gradient header banner */}
                <div style={{
                    background: 'linear-gradient(135deg, #262262 0%, #477CBF 100%)',
                    borderRadius: '8px 8px 0 0',
                    padding: '24px 28px 20px',
                    textAlign: 'center',
                }}>
                    <BellFilled style={{ fontSize: 40, color: '#f79421', marginBottom: 10 }} />
                    <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>
                        THÍ SINH LƯU Ý
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 4 }}>
                        Vui lòng đọc kỹ trước khi điền hồ sơ
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '20px 28px 16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                        {/* Item 1 */}
                        <div style={{
                            background: '#f0f4ff',
                            border: '1px solid #d0deff',
                            borderRadius: 8,
                            padding: '12px 16px',
                        }}>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <EnvironmentOutlined style={{ color: '#477CBF', fontSize: 18, marginTop: 2, flexShrink: 0 }} />
                                <div>
                                    <div style={{ fontWeight: 600, color: '#262262', marginBottom: 6 }}>
                                        Bắt buộc đăng ký trên Cổng thông tin Bộ Giáo dục và Đào tạo từ 02/7/2026 đến 17h00 ngày 14/7/2026
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <CheckCircleFilled style={{ color: '#f79421', fontSize: 18, flexShrink: 0 }} />
                                            <span style={{ color: '#444' }}>Học tại Hà Nội thì chọn mã</span>
                                            <span style={{
                                                background: '#f79421', color: '#fff',
                                                borderRadius: 4, padding: '1px 8px',
                                                fontSize: 13, fontWeight: 700, flexShrink: 0
                                            }}>GTA</span>                                            
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <CheckCircleFilled style={{ color: '#f79421', fontSize: 18, flexShrink: 0 }} />
                                            <span style={{ color: '#444' }}>Học tại Phú Thọ thì chọn mã</span>
                                            <span style={{
                                                background: '#477CBF', color: '#fff',
                                                borderRadius: 4, padding: '1px 8px',
                                                fontSize: 13, fontWeight: 700, flexShrink: 0
                                            }}>GTP</span>
                                            
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>                       

                        {/* Item 3 */}
                        <div style={{
                            background: '#fffbf0',
                            border: '1px solid #ffe58f',
                            borderRadius: 8,
                            padding: '12px 16px',
                            display: 'flex', gap: 10, alignItems: 'center',
                        }}>
                            <CloseOutlined style={{ color: 'red', fontSize: 18, flexShrink: 0 }} />                                            
                            <span style={{ color: '#444' }}>
                                <strong>Không</strong> cần nộp giấy tờ bản cứng học bạ về Trường
                            </span>
                        </div>

                    </div>
                </div>
            </Modal>

            <Modal
                open={showNoticeModal}
                title={
                    <Space>
                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Thông báo quan trọng</span>
                    </Space>
                }
                closable={false}
                maskClosable={false}
                centered
                footer={
                    <Button
                        type="primary"
                        size="large"
                        onClick={() => {
                            setShowNoticeModal(false);
                            navigate('/payment');
                        }}
                        style={{ width: '100%' }}
                    >
                        Đã hiểu, tiếp tục nộp lệ phí →
                    </Button>
                }
            >
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={<strong>Yêu cầu bắt buộc sau khi đăng ký</strong>}
                    description={
                        <p style={{ margin: 0, lineHeight: '1.7' }}>
                            Thí sinh phải <strong>ĐĂNG KÝ NGUYỆN VỌNG CHÍNH THỨC</strong> trên hệ thống của{' '}
                            <strong>Bộ Giáo dục và Đào tạo</strong> từ ngày <strong>02/7/2026</strong> đến{' '}
                            <strong>17h00 ngày 14/7/2026</strong> theo quy định{' '}
                            (có thể điều chỉnh các nguyện vọng đã đăng ký dự tuyển trên hệ thống của Nhà trường).
                        </p>
                    }
                />
            </Modal>

            {initialLoading ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                    <Spin size="large" />
                </div>
            ) : periodLocked ? (
                <StudentProfileReadOnly
                    data={studentProfileData}
                    attachmentTypes={attachmentTypes}
                />
            ) : (
                <>
                    <Title level={4} style={{ textAlign: 'center', marginBottom: 30 }}>
                        Thông tin đăng ký dự tuyển
                    </Title>

                    <StudentProfileFormSteps
                        form={form}
                        subjects={data.subjects}
                        periods={data.periods}
                        gradeFields={data.gradeFields}
                        submitLoading={loading}
                        onFinish={onFinish}
                        initialValues={data.initialValues}
                        uploadProps={uploadProps}
                        uploadLoading={uploadLoading}
                        uploadProgress={uploadProgress}
                        uploadStatus={uploadStatus}
                        fileList={fileList}
                        attachmentTypes={attachmentTypes}
                        subjectTypes={data.subjectTypes}
                        studentProfileData={studentProfileData}
                        onFileSelect={handleFileSelect}
                        onManualUpload={handleManualUpload}
                        onBatchUpload={handleBatchUpload}
                    />
                </>
            )}
        </>
    );

    // Check if user is authenticated and determine which layout to use
    const auth = checkAuth();
    if (!auth.isAuthenticated) {
        return null; // Return null if not authenticated (redirect is handled in checkAuth)
    }

    // Use appropriate layout based on user role
    return auth.isAdmin ? (
        <AdminLayout title="Thông tin đăng ký dự tuyển">
            {content}
        </AdminLayout>
    ) : (
        <UserLayout title="Trường Đại học Công nghệ giao thông vận tải (GTA)">
            {content}
        </UserLayout>
    );
};

export default StudentProfile;